import { Dexie } from 'dexie';
import axios from 'axios';

class TalismanTokenRatesDatabase extends Dexie {
  constructor() {
    super("TalismanTokenRates");

    // https://dexie.org/docs/Tutorial/Design#database-versioning
    this.version(1).stores({
      // You only need to specify properties that you wish to index.
      // The object store will allow any properties on your stored objects but you can only query them by indexed properties
      // https://dexie.org/docs/API-Reference#declare-database
      //
      // Never index properties containing images, movies or large (huge) strings. Store them in IndexedDB, yes! but just don’t index them!
      // https://dexie.org/docs/Version/Version.stores()#warning
      tokenRates: "tokenId"
    });
  }
}
const db = new TalismanTokenRatesDatabase();

const NewTokenRates = () => ({
  usd: null,
  aud: null,
  nzd: null,
  cud: null,
  hkd: null,
  eur: null,
  gbp: null,
  jpy: null,
  krw: null,
  cny: null,
  rub: null,
  btc: null,
  eth: null,
  dot: null
});

// every currency in this list will be fetched from coingecko
// comment out unused currencies to save some bandwidth!
const coingeckoCurrencies = ["usd", "aud",
// 'nzd',
// 'cud',
// 'hkd',
"eur", "gbp", "jpy",
// 'krw',
"cny", "rub", "btc", "eth", "dot"];
const DEFAULT_COINGECKO_CONFIG = {
  apiUrl: "https://api.coingecko.com"
};

// export function tokenRates(tokens: WithCoingeckoId[]): TokenRatesList {}
async function fetchTokenRates(tokens, config = DEFAULT_COINGECKO_CONFIG) {
  // create a map from `coingeckoId` -> `tokenId` for each token
  const coingeckoIdToTokenIds = Object.values(tokens)
  // ignore testnet tokens
  .filter(({
    isTestnet
  }) => !isTestnet)

  // ignore tokens which don't have a coingeckoId
  .filter(hasCoingeckoId)

  // get each token's coingeckoId
  .reduce((coingeckoIdToTokenIds, {
    id,
    coingeckoId
  }) => {
    if (!coingeckoIdToTokenIds[coingeckoId]) coingeckoIdToTokenIds[coingeckoId] = [];
    coingeckoIdToTokenIds[coingeckoId].push(id);
    return coingeckoIdToTokenIds;
  }, {});

  // create a list of coingeckoIds we want to fetch
  const coingeckoIds = Object.keys(coingeckoIdToTokenIds);

  // skip network request if there is nothing for us to fetch
  if (coingeckoIds.length < 1) return {};

  // construct a coingecko request, sort args to help proxies with caching
  const idsSerialized = coingeckoIds.sort().join(",");
  const currenciesSerialized = coingeckoCurrencies.sort().join(",");
  // note: coingecko api key cannot be passed as header here as it would be camel cased by axios and ignored by the server
  // need to pass it as a query parameter, and replace all '-' with '_'
  // TODO => migrate to fetch api
  const apiKeySuffix = config.apiKeyName && config.apiKeyValue ? `&${config.apiKeyName?.replaceAll("-", "_")}=${config.apiKeyValue}` : "";
  const queryUrl = `${config.apiUrl}/api/v3/simple/price?ids=${idsSerialized}&vs_currencies=${currenciesSerialized}${apiKeySuffix}`;

  // fetch the token prices from coingecko
  // the response should be in the format:
  // {
  //   [coingeckoId]: {
  //     [currency]: rate
  //   }
  // }
  const coingeckoPrices = await axios.get(queryUrl).then(response => response.data);

  // build a TokenRatesList from the token prices result
  const ratesList = Object.fromEntries(coingeckoIds.flatMap(coingeckoId => {
    const tokenIds = coingeckoIdToTokenIds[coingeckoId];
    const rates = NewTokenRates();
    for (const currency of coingeckoCurrencies) {
      rates[currency] = ((coingeckoPrices || {})[coingeckoId] || {})[currency] || null;
    }
    return tokenIds.map(tokenId => [tokenId, rates]);
  }));

  // return the TokenRatesList
  return ratesList;
}
function hasCoingeckoId(token) {
  return "coingeckoId" in token && typeof token.coingeckoId === "string";
}

export { DEFAULT_COINGECKO_CONFIG, NewTokenRates, TalismanTokenRatesDatabase, db, fetchTokenRates, hasCoingeckoId };
